import AttachMoney from "@mui/icons-material/AttachMoney";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Button,
  ClickAwayListener,
  Fade,
  IconButton,
  List,
  Paper,
  Popper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import {
  MenuDefinition,
  menuDefinitions,
  menuNames,
  menuType,
} from "pages/home/menu";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import theme from "theme/default";
import {
  borderRadius,
  boxShadows,
  colors,
  fontSize,
  fontWeight,
  gaps,
  gradients,
  margin,
  padding,
  zIndex,
} from "theme/defaultStyle";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import PaymentsHeaderDropDown from "./PaymentsHeaderDropDown";

export type TopNavBarProps = {
  saveFileRef?: any;
  saveFileChangeRef?: any;
  onNavigate: (url: string) => void;
};

const linkButtonHeightMin = "30px";
const linkButtonHeightMax = "122px";

const NavItemsContainer = styled(Box)((props) => ({
  "div.MuiBox-root": {
    margin: `${margin.zero} ${margin.large19}`,
    width: "auto",
    "& a div.MuiBox-root": {
      margin: `${margin.zero} ${margin.zero}`,
    },
  },
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: gaps.small1,
  justifyContent: "center",
  padding: padding.zero,
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    height: linkButtonHeightMin,
    order: 2,
    "& > :first-child": {
      width: "73px",
    },
  },
  ".rectangle-active": {
    background: colors.white,
    width: "100%",
    height: "4px",
    gap: gaps.zero,
    borderRadius: `${borderRadius.small} ${borderRadius.small} ${borderRadius.zero} ${borderRadius.zero}`,
    marginBottom: `-${margin.small_5} !important`,
    marginTop: `${margin.xsmall1} !important`,
  },
  "@media (max-width:1279px)": {
    ".rectangle-active": {
      marginBottom: `-${margin.large1} !important`,
      marginTop: `${margin.medium} !important`,
    },
  },
  ".rectangle-none": {
    display: "none",
  },
  "div.MuiBox-root:not(.active):not([title]) > a >div": {
    position: "relative",
    top: gaps.xsmall1,
  },
}));

const StyledNavButton = styled(Box)((props) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  height: linkButtonHeightMin,
  justifyContent: "center",
  padding: padding.zero,
  width: linkButtonHeightMin,

  "& a": {
    borderRadius: borderRadius.small,
    color: colors.white,
    fontSize: fontSize.base,
    fontWeight: fontWeight.normal1,
    lineHeight: 1,
    padding: padding.zero,
    textDecoration: "none",
    textTransform: "initial",
    "&:hover": {
      color: colors.blue12,
      textDecoration: "none",
    },
    "&.disabledLink": {
      color: colors.blue12,
      cursor: "default",
      lineHeight: 1,
      "& .hover-content": {
        display: "none",
      },
      "&:hover": {
        color: colors.blue12,
      },
    },
    "&.disabledLink:hover ": {
      cursor: "default",
    },
    "&.disabledLink:hover .normal-content": {
      display: "none",
    },
    "&.disabledLink:hover .hover-content": {
      display: "flex",
    },
    "& div.MuiBox-root > div.MuiBox-root:not([title])": {
      background: `${colors.black} !important`,
    },
  },
  "& a .icon": {
    color: colors.white,
    fontSize: fontSize.xlarge2,
    "&:hover": {
      color: colors.blue12,
      textDecoration: "none",
    },
  },
  "& .disabledLink .icon": {
    color: colors.blue12,
    fontSize: fontSize.xlarge2,
  },
  "& a div": {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: gaps.xsmall2,
    justifyContent: "center",
    padding: padding.xsmall,
  },
  "&:hover": {
    color: colors.blue12,
  },
  "&.disabled": {
    cursor: "default",
    opacity: 1,
  },
  "& div.MuiToolbar-root div.MuiBox-root": {
    borderBottom: `4px solid ${colors.white}`,
  },
  [theme.breakpoints.up("lg")]: {
    width: linkButtonHeightMax,
  },
}));

const StyledMobileIconButton = styled(IconButton)({
  alignSelf: "center",
  color: colors.white,
  display: "flex",
  "& svg:first-of-type": {
    height: "24px",
    width: "24px",
  },
});

const StyledMobileNavLabel = styled(Typography)({
  color: colors.white,
  fontFamily: "Inter",
  fontSize: fontSize.medium,
  fontWeight: `${fontWeight.bold1} !important`,
  margin: `${margin.zero} ${margin.small_5}`,
});

const StyledMobileNavPaper = styled(Paper)({
  overflow: "visible",
  borderRadius: borderRadius.xlarge,
  position: "relative",
  "&:after": {
    border: "10px solid transparent",
    borderBottomColor: colors.blue20,
    bottom: "100%",
    content: '""',
    height: 0,
    left: "50%",
    marginLeft: `-${margin.small2}`,
    pointerEvents: "none",
    position: "absolute",
    width: 0,
  },
  "& ul.MuiList-root": {
    background: gradients.blueGradient08,
    borderRadius: borderRadius.xlarge,
    boxShadow: boxShadows.boxShadow02,
    padding: `${padding.large1} ${padding.large1} !important`,
  },
  "& svg": {
    height: "24px",
    width: "24px",
  },
});

const StyledMobileNavItem = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ isActive }) => ({
  background: isActive ? gradients.blueGradient10 : "transparent",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  padding: `${padding.large1} ${padding.large1}`,
  position: "relative",
  width: "100%",
  "&:before": {
    content: '""',
    height: 0,
    width: 0,
  },
}));

const StyledNavItemLabel = styled(Typography)({
  color: colors.white,
  fontFamily: "Inter",
  fontSize: `${fontSize.medium} !important`,
  fontWeight: `${fontWeight.bold1} !important`,
  letterSpacing: "0.17px",
  lineHeight: "16.94px !important",
  marginLeft: margin.small2,
  textTransform: "none",
  "&:hover": {
    color: colors.blue12,
  },
});

const renderMenuItem = (
  { menuId, menuName, url, disabled, icon, headerIcon }: MenuDefinition,
  onNavigate: Function,
  showButtonLabels: boolean,
  active: boolean,
) => {
  const Icon = headerIcon ?? icon;
  const className = clsx(active ? "active" : "", disabled ? "disabled" : "");

  return (
    <StyledNavButton
      key={menuId}
      onClick={() => !disabled && onNavigate(url)}
      className={className}
      title={menuName}
    >
      {disabled ? (
        <Link
          data-menu-id={menuId}
          onClick={(event) => event.preventDefault()}
          to="/"
          className="disabledLink"
        >
          <Box className="normal-content">
            {Icon && <Icon className="icon" />}
            {showButtonLabels && menuName}
          </Box>
          <Box className="hover-content">
            {Icon && <Icon className="icon" />}
            {showButtonLabels && menuName}
          </Box>
        </Link>
      ) : (
        <Link
          data-menu-id={menuId}
          to={url}
          onClick={(event) => event.preventDefault()}
          className={active ? "active" : ""}
        >
          <Box>
            {Icon && <Icon className="icon" />}
            {showButtonLabels && menuName}
          </Box>
          <Box className={active ? "rectangle-active" : "rectangle-none"}></Box>
        </Link>
      )}
    </StyledNavButton>
  );
};

const TopNavBar = ({ onNavigate }: TopNavBarProps) => {
  const {
    featureConfig: { enableReportPay },
  } = useConfigContext();
  const [{ profileSettings }] = useProfileSettingsCache();
  const showButtonLabels = useMediaQuery(theme.breakpoints.up("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [isMobileOpen, setMobileOpen] = useState(false);
  const navItemAnchor = useRef<HTMLButtonElement>(null);

  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];

  const filteredItems = menuDefinitions.filter(
    (menu) => menu.type === menuType.mainMenu && menu.isVisibleInHeader && permissionCodes?.includes(menu.permissionCode!),
  );

  const mobileNavItems = filteredItems.flatMap((item) => {
    if (item.menuName !== menuNames.lblMenuPayments) {
      return [item];
    }

    return [
      {
        ...item,
        disabled: !enableReportPay,
        headerIcon: AttachMoney,
        menuName: menuNames.lblMenuPaymentsReportPay,
        url: "/reportpay",
      },
      {
        ...item,
        disabled: !enableReportPay,
        headerIcon: AttachMoney,
        menuName: menuNames.lblMenuPaymentsPendingPayments,
        url: "/pendingpay",
      },
    ];
  });

  const activePath = window.location.pathname;
  const activeItem = mobileNavItems.find((item) => item.url === activePath) ?? filteredItems.find((item) => item.url === activePath);
  const ActiveIcon = activeItem?.headerIcon ?? activeItem?.icon;

  useEffect(() => {
    setMobileOpen(false);
  }, [isMobile]);

  const handleCloseNavDropdown = (event: any) => {
    if (navItemAnchor.current && navItemAnchor.current.contains(event.target as HTMLElement)) {
      return;
    }
    setMobileOpen(false);
  };

  if (isMobile) {
    return (
      <>
        <StyledMobileIconButton
          disableRipple
          color="inherit"
          onClick={() => setMobileOpen(!isMobileOpen)}
          ref={navItemAnchor}
        >
          {activeItem ? (
            <>
              {ActiveIcon && <ActiveIcon />}
              <StyledMobileNavLabel>
                {activeItem.menuName}
              </StyledMobileNavLabel>
              {!isMobileOpen ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
            </>
          ) : (
            <MenuIcon />
          )}
        </StyledMobileIconButton>

        <Popper
          open={isMobileOpen}
          anchorEl={navItemAnchor.current}
          disablePortal
          transition
          style={{ zIndex: zIndex.high3 }}
          modifiers={[{ name: "offset", options: { offset: [0, 17] } }]}
        >
          {({ TransitionProps }: any) => (
            <Fade {...TransitionProps} timeout={200}>
              <StyledMobileNavPaper>
                <ClickAwayListener onClickAway={handleCloseNavDropdown}>
                  <List disablePadding>
                    {mobileNavItems.map((item) => {
                      const Icon = item.headerIcon ?? item.icon;
                      const active = activePath === item.url;
                      return (
                        <StyledMobileNavItem
                          key={`${item.menuId}-${item.url}`}
                          disabled={item.disabled}
                          color="inherit"
                          isActive={active}
                          onClick={() => {
                            setMobileOpen(false);
                            onNavigate(item.url);
                          }}
                        >
                          {Icon && <Icon sx={{ color: colors.white }} />}
                          <StyledNavItemLabel variant="subtitle1">
                            {item.menuName}
                          </StyledNavItemLabel>
                        </StyledMobileNavItem>
                      );
                    })}
                  </List>
                </ClickAwayListener>
              </StyledMobileNavPaper>
            </Fade>
          )}
        </Popper>
      </>
    );
  }

  const renderedItems = filteredItems.map((item) => {
    if (item.menuName === menuNames.lblMenuPayments) {
      item.disabled = !enableReportPay;
      if (enableReportPay) {
        return <PaymentsHeaderDropDown onNavigate={onNavigate} />;
      }
    }
    const active = activePath === item.url;
    return renderMenuItem(
      item,
      onNavigate,
      showButtonLabels,
      active,
    );
  });
  return <NavItemsContainer id="navBar">{renderedItems}</NavItemsContainer>;
};

export default TopNavBar;
